import dayjs, { type Dayjs } from 'dayjs';
import {
  lazy,
  Suspense,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { querySuiteRuns } from '@/features/executions/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { DetailMetricsStatusChartSection } from '@/features/shared/components/chartPanels/DetailMetricsStatusChartSection';
import { DeferredRenderBlock } from '@/features/shared/components/deferred/DeferredRenderBlock';
import { DeferredTablePanelSkeleton } from '@/features/shared/components/deferred/DeferredSectionSkeletons';
import { DetailDeleteHeaderAction } from '@/features/shared/components/entityDetails/DetailDeleteHeaderAction';
import { DetailHeaderIconButton } from '@/features/shared/components/entityDetails/DetailHeaderIconButton';
import { DetailTablePanel } from '@/features/shared/components/tableSystem/DetailTablePanel';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import {
  collapseStatusOptions,
  expandStatusFilterValues,
} from '@/features/shared/utils/projectStatusPresentation';
import { SuiteDetailsPanels } from '@/features/suites/components/SuiteDetailsPanels';
import {
  suiteRunDefaultSortDirection,
  suiteRunDefaultSortKey,
  suiteRunSortFieldMap,
  suiteRunSortOptions,
  suiteRunStatusOptions,
  type SuiteRunSortDirection,
  type SuiteRunSortKey,
} from '@/features/suites/pages/suiteRunsTableDefinition';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { queryTests } from '@/features/tests/data/tests';
import {
  testsTableDefinition,
  type TestSortDirection,
  type TestSortKey,
} from '@/features/tests/pages/testsTableDefinition';
import { suiteRunSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { detailPageSectionGapToken } from '@/ui/composites/scaffolds/detailPageSpacing';
import { Inline } from '@/ui/layout/Inline';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives/Text';
import type { SuiteStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { SuiteDetailPageViewModel } from '@/features/suites/hooks/useSuiteDetailPageViewModel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type {
  QueryResult,
  SuiteDetailDto,
  SuiteRunDto,
  SuiteRunStatus,
  TestRunStatus,
  TestListItemDto,
} from '@flowqa/shared';

type SuiteDetailContentProps = {
  suite: SuiteDetailDto;
  viewModel: SuiteDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  onEdit?: () => void;
  deleteLoading?: boolean;
  editLoading?: boolean;
  editDialog?: ReactElement | null;
};

const SuiteRunsTable = lazy(async () => {
  const module = await import('@/features/suites/components/SuiteRunsTable');
  return { default: module.SuiteRunsTable };
});

export const SuiteDetailContent = ({
  suite,
  viewModel,
  onDelete,
  onEdit,
  deleteLoading = false,
  editLoading = false,
  editDialog = null,
}: SuiteDetailContentProps): ReactElement => {
  const runTableDetailColumnOptions = useMemo(
    () => [
      { field: 'runNumber', label: getDetailLabel('run'), mandatory: true },
      { field: 'status', label: getDetailLabel('status') },
      { field: 'tests', label: getDetailLabel('tests') },
      { field: 'output', label: getDetailLabel('output') },
      { field: 'runId', label: getDetailLabel('details') },
    ],
    [],
  );
  const runTableDetailDefaultColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      runNumber: true,
      status: true,
      tests: true,
      output: true,
      runId: true,
    }),
    [],
  );
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const { selectedOrganisationId } = useOrganisationFilter();
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const detailProjectId = suite.projectId;
  const [detailDateQuickFilter, setDetailDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [detailFromDate, setDetailFromDate] = useState<Dayjs | null>(null);
  const [detailToDate, setDetailToDate] = useState<Dayjs | null>(null);
  const [debouncedRunTableSearch, setDebouncedRunTableSearch] = useState('');
  const [runTableColumnVisibilityModel, setRunTableColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(runTableDetailDefaultColumnVisibilityModel);
  const [runTableSortKey, setRunTableSortKey] = useState<SuiteRunSortKey>(suiteRunDefaultSortKey);
  const [runTableSortDirection, setRunTableSortDirection] = useState<SuiteRunSortDirection>(
    suiteRunDefaultSortDirection,
  );
  const [runTableFilterStatuses, setRunTableFilterStatuses] = useState<SuiteRunStatus[]>([]);
  const [runTableFilterTags, setRunTableFilterTags] = useState<string[]>([]);
  const [debouncedTestsTableSearch, setDebouncedTestsTableSearch] = useState('');
  const [testsTableColumnVisibilityModel, setTestsTableColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(testsTableDefinition.defaultColumnVisibilityModel);
  const [testsTableSortKey, setTestsTableSortKey] = useState<TestSortKey>(
    testsTableDefinition.defaultSortKey,
  );
  const [testsTableSortDirection, setTestsTableSortDirection] = useState<TestSortDirection>(
    testsTableDefinition.defaultSortDirection,
  );
  const [testsTableFilterStatuses, setTestsTableFilterStatuses] = useState<TestRunStatus[]>([]);
  const [testsTableFilterTags, setTestsTableFilterTags] = useState<string[]>([]);
  const [runTableMeta, setRunTableMeta] = useState<TableQueryMeta<SuiteRunDto> | null>(null);
  const [testsTableMeta, setTestsTableMeta] = useState<TableQueryMeta<TestListItemDto> | null>(
    null,
  );

  useEffect(() => {
    const nextQuickFilter: DateQuickFilter =
      viewModel.range === '14d' || viewModel.range === '30d' || viewModel.range === 'latest'
        ? viewModel.range
        : '7d';
    setDetailDateQuickFilter(nextQuickFilter);
    setDetailFromDate(viewModel.selectedStartDay ? dayjs(viewModel.selectedStartDay) : null);
    setDetailToDate(viewModel.selectedEndDay ? dayjs(viewModel.selectedEndDay) : null);
  }, [viewModel.range, viewModel.selectedEndDay, viewModel.selectedStartDay]);

  const detailDateWindow = useDateWindow({
    dateQuickFilter: detailDateQuickFilter,
    fromDate: detailFromDate,
    toDate: detailToDate,
  });
  const runTableAvailableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const testsTableAvailableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const suiteRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suiteRun' });
  const testRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'testRun' });
  const runTableNoRowsText = detailDateWindow.hasCustomBounds
    ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'suiteRun' })
    : formatRangeWindowNoDataText(suiteRunsEmptyState, detailDateQuickFilter, false);
  const testsTableNoRowsText = detailDateWindow.hasCustomBounds
    ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'testRun' })
    : formatRangeWindowNoDataText(testRunsEmptyState, detailDateQuickFilter, false);
  const runTableStatusFilterKey = useMemo(
    () => [...runTableFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [runTableFilterStatuses],
  );
  const runTableTagFilterKey = useMemo(
    () => [...runTableFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [runTableFilterTags],
  );
  const runTableInstanceKey = useMemo(
    () =>
      [
        viewModel.suiteId,
        detailDateQuickFilter,
        detailDateWindow.startDay ?? 'none',
        detailDateWindow.endDay ?? 'none',
        runTableStatusFilterKey,
        runTableTagFilterKey,
        debouncedRunTableSearch,
      ].join(':'),
    [
      debouncedRunTableSearch,
      detailDateQuickFilter,
      detailDateWindow.endDay,
      detailDateWindow.startDay,
      runTableStatusFilterKey,
      runTableTagFilterKey,
      viewModel.suiteId,
    ],
  );
  const testsTableStatusFilterKey = useMemo(
    () => [...testsTableFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [testsTableFilterStatuses],
  );
  const testsTableTagFilterKey = useMemo(
    () => [...testsTableFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [testsTableFilterTags],
  );
  const effectiveRunTableFilterStatuses = useMemo(
    () => expandStatusFilterValues(runTableFilterStatuses, groupErroredWithFailed),
    [groupErroredWithFailed, runTableFilterStatuses],
  );
  const effectiveTestsTableFilterStatuses = useMemo(
    () => expandStatusFilterValues(testsTableFilterStatuses, groupErroredWithFailed),
    [groupErroredWithFailed, testsTableFilterStatuses],
  );
  const detailStatusOptions = useMemo(
    () => collapseStatusOptions(suiteRunSummaryStatusOptions, groupErroredWithFailed),
    [groupErroredWithFailed],
  );
  const runTableStatusOptions = useMemo(
    () => collapseStatusOptions(suiteRunStatusOptions, groupErroredWithFailed),
    [groupErroredWithFailed],
  );
  const testsTableStatusOptions = useMemo(
    () => collapseStatusOptions(testsTableDefinition.statusOptions, groupErroredWithFailed),
    [groupErroredWithFailed],
  );
  const runTableDateBounds = useMemo(
    () => resolveMetaDateBounds(runTableMeta, (item) => item.timestamp),
    [runTableMeta],
  );
  const runTableFixedFilters = useMemo(() => {
    const isLatestQuickFilter =
      detailDateQuickFilter === 'latest' && detailFromDate === null && detailToDate === null;
    return mergeFilters(
      buildEqFilterValue('suiteId', viewModel.suiteId),
      buildInFilter('status', effectiveRunTableFilterStatuses),
      buildInFilter('tags', runTableFilterTags),
      isLatestQuickFilter
        ? buildEqFilterValue('isLatest', true)
        : buildDateRangeFilters(
            'timestamp',
            detailDateWindow.startDay
              ? dayjs(detailDateWindow.startDay).startOf('day').toISOString()
              : null,
            detailDateWindow.endDay
              ? dayjs(detailDateWindow.endDay).endOf('day').toISOString()
              : null,
          ),
    );
  }, [
    detailDateQuickFilter,
    detailDateWindow.endDay,
    detailDateWindow.startDay,
    detailFromDate,
    detailToDate,
    effectiveRunTableFilterStatuses,
    runTableFilterTags,
    viewModel.suiteId,
  ]);
  const runTableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteRunDto>> => {
      const sortOverride: ApiSort = {
        field: suiteRunSortFieldMap[runTableSortKey],
        direction: runTableSortDirection,
      };
      return querySuiteRuns(detailProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedRunTableSearch,
        sorts: [sortOverride],
      });
    },
    [
      debouncedRunTableSearch,
      detailProjectId,
      runTableSortDirection,
      runTableSortKey,
      selectedOrganisationId,
    ],
  );
  const testsTableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestRunStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildEqFilterValue('suiteId', viewModel.suiteId),
        buildInFilter('latestRunStatus', effectiveTestsTableFilterStatuses),
        buildInFilter('tags', testsTableFilterTags),
      );
      const sortOverride: ApiSort = {
        field: testsTableDefinition.sortFieldMap[testsTableSortKey],
        direction: testsTableSortDirection,
      };
      return queryTests(detailProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedTestsTableSearch,
        sorts: [sortOverride],
        filters: [...mappedFilters, ...toolbarFilters],
        metricsStartDay: detailDateWindow.startDay ?? undefined,
        metricsEndDay: detailDateWindow.endDay ?? undefined,
        previousMetricsStartDay: detailDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: detailDateWindow.previousEndDay ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestRun: true,
          executionStatusCounts: true,
          executionSummaries: true,
          metrics: true,
          metricDeltas: viewModel.showTableDeltas,
        },
      });
    },
    [
      debouncedTestsTableSearch,
      detailProjectId,
      detailDateWindow.endDay,
      detailDateWindow.previousEndDay,
      detailDateWindow.previousStartDay,
      detailDateWindow.startDay,
      effectiveTestsTableFilterStatuses,
      selectedOrganisationId,
      testsTableFilterTags,
      testsTableSortDirection,
      testsTableSortKey,
      viewModel.showTableDeltas,
      viewModel.suiteId,
    ],
  );
  const suiteRunsNoDataText = formatRangeWindowNoDataText(
    suiteRunsEmptyState,
    viewModel.range,
    hasCustomDates,
  );
  const availableTags = useMemo(
    () =>
      Array.from(new Set(viewModel.tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [viewModel.tagOptions],
  );
  const handleMetricStatusesChange = useCallback(
    (nextStatuses: SuiteStatusKey[]): void => {
      viewModel.handleStatusesChange(nextStatuses);
      setRunTableFilterStatuses(nextStatuses);
    },
    [viewModel.handleStatusesChange],
  );
  const metricsToolbarState = useMemo<MetricsStatusChartPanelModelToolbarState<SuiteStatusKey>>(
    () => ({
      search: '',
      setSearch: () => undefined,
      selectedStatuses: viewModel.selectedStatuses,
      setSelectedStatuses: handleMetricStatusesChange,
      selectedTags: viewModel.selectedTags,
      setSelectedTags: viewModel.handleTagsChange,
      noRowsText: suiteRunsNoDataText,
      latestQueryStartDay: viewModel.pickerStartDay,
      latestQueryEndDay: viewModel.pickerEndDay,
      dateQuickFilter:
        viewModel.range === '14d' || viewModel.range === '30d' || viewModel.range === 'latest'
          ? viewModel.range
          : '7d',
      onDateQuickFilterChange: (next) => {
        viewModel.setRange(next);
        viewModel.setSelectedStartDay(null);
        viewModel.setSelectedEndDay(null);
      },
      fromDate: viewModel.selectedStartDay ? dayjs(viewModel.selectedStartDay) : null,
      toDate: viewModel.selectedEndDay ? dayjs(viewModel.selectedEndDay) : null,
      onFromDateChange: viewModel.handleStartDayChange,
      onToDateChange: viewModel.handleEndDayChange,
    }),
    [
      suiteRunsNoDataText,
      viewModel.handleEndDayChange,
      viewModel.handleStartDayChange,
      viewModel.handleTagsChange,
      handleMetricStatusesChange,
      viewModel.pickerEndDay,
      viewModel.pickerStartDay,
      viewModel.range,
      viewModel.selectedEndDay,
      viewModel.selectedStatuses,
      viewModel.selectedStartDay,
      viewModel.selectedTags,
      viewModel.setRange,
      viewModel.setSelectedEndDay,
      viewModel.setSelectedStartDay,
    ],
  );
  return (
    <>
      <DetailPageTemplate
        pageTitle={viewModel.title}
        title={
          <Text role="sectionTitle" component="h1">
            <DetailTitlePrefix label="Suites" href="/suites" />
            <Text role="sectionTitle" component="span">
              {viewModel.title}
            </Text>
          </Text>
        }
        headerActions={
          <Inline gap="md" align="center">
            <DetailHeaderIconButton
              label="Edit Suite"
              disabled={editLoading}
              onClick={onEdit ?? (() => undefined)}
            />
            <DetailDeleteHeaderAction
              onDelete={onDelete}
              deleteLoading={deleteLoading}
              dialogTitle="Delete Suite"
              dialogDescription={
                <Stack gap="sm">
                  <Text role="body" color="textSecondary">
                    Deleting this suite also removes associated tests, suite runs, and test runs.
                  </Text>
                  <Text role="body" color="textSecondary">
                    {getDialogMessage('destructive.cannotBeUndone')}
                  </Text>
                </Stack>
              }
              confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
                entity: 'suite',
              })}
            />
          </Inline>
        }
        metadata={
          <SuiteDetailsPanels suite={suite} metricValueItems={viewModel.metricValueItems} />
        }
        primaryContent={
          <Section paddingY="none">
            <Stack gap={detailPageSectionGapToken}>
              <DetailMetricsStatusChartSection<SuiteStatusKey>
                title={getChartTitle('status')}
                searchAriaLabel="Search suite run metrics"
                noRowsBaseText={suiteRunsEmptyState}
                scope="suite"
                projectId={suite.projectId}
                availableTags={availableTags}
                metricCards={viewModel.suiteMetricCards}
                metricValueItems={viewModel.metricValueItems}
                loading={viewModel.isChartLoading}
                statusCardSkeletonCount={5}
                toolbarState={metricsToolbarState}
                statusOptions={detailStatusOptions}
                suppressAnimation={viewModel.suppressChartAnimation}
                showPoints={viewModel.showLineChartPoints}
                stackedChartData={viewModel.stackedChartData}
                passRateChartData={viewModel.passRateChartData}
                frequencyChartData={viewModel.frequencyChartData}
                durationChartData={viewModel.durationChartData}
                flakeScoreChartData={viewModel.flakeScoreChartData}
              />
              <DeferredRenderBlock
                fallback={<DeferredTablePanelSkeleton titleWidth={126} variant="suiteRuns" />}
                minHeight="24rem"
              >
                <DetailTablePanel
                  tabLabel="Runs"
                  tabsAriaLabel="Suite detail runs panel"
                  toolbarSlot={
                    <TableToolbar<SuiteRunSortKey, SuiteRunStatus>
                      title={undefined}
                      searchAriaLabel="Search suite runs"
                      searchValue={debouncedRunTableSearch}
                      onDebouncedSearchChange={setDebouncedRunTableSearch}
                      columnVisibilityModel={runTableColumnVisibilityModel}
                      defaultColumnVisibilityModel={runTableDetailDefaultColumnVisibilityModel}
                      columnOptions={runTableDetailColumnOptions}
                      onColumnVisibilityModelChange={setRunTableColumnVisibilityModel}
                      sortKey={runTableSortKey}
                      sortDirection={runTableSortDirection}
                      defaultSortKey={suiteRunDefaultSortKey}
                      defaultSortDirection={suiteRunDefaultSortDirection}
                      sortOptions={suiteRunSortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setRunTableSortKey(nextSortKey);
                        setRunTableSortDirection(nextSortDirection);
                      }}
                      filterStatuses={runTableFilterStatuses}
                      statusOptions={runTableStatusOptions}
                      onFilterStatusesChange={setRunTableFilterStatuses}
                      filterTags={runTableFilterTags}
                      onFilterTagsChange={setRunTableFilterTags}
                      dateQuickFilter={detailDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setDetailDateQuickFilter(next);
                        setDetailFromDate(null);
                        setDetailToDate(null);
                      }}
                      fromDate={detailFromDate}
                      toDate={detailToDate}
                      onFromDateChange={setDetailFromDate}
                      onToDateChange={setDetailToDate}
                      availableTags={runTableAvailableTags}
                      includeLatestQuickFilter
                      statusFilterLabel={getChartTitle('status')}
                      summaryCount={runTableMeta?.total ?? null}
                      summarySingularLabel="suite run"
                      summaryPluralLabel="suite runs"
                      previewFromDate={
                        detailDateQuickFilter === 'latest' ? runTableDateBounds.from : null
                      }
                      previewToDate={
                        detailDateQuickFilter === 'latest' ? runTableDateBounds.to : null
                      }
                    />
                  }
                >
                  <Suspense
                    fallback={<DeferredTablePanelSkeleton titleWidth={126} variant="suiteRuns" />}
                  >
                    <SuiteRunsTable
                      key={runTableInstanceKey}
                      persistKey={null}
                      hideNameColumn
                      runColumnFirst
                      includeSummary
                      tagOptionsOverride={viewModel.tagOptions}
                      queryKey={[
                        'suite-detail-executions-table',
                        viewModel.suiteId,
                        selectedOrganisationId ?? null,
                        detailProjectId,
                        detailDateQuickFilter,
                        runTableSortKey,
                        runTableSortDirection,
                        runTableStatusFilterKey,
                        runTableTagFilterKey,
                        debouncedRunTableSearch,
                        detailDateWindow.startDay,
                        detailDateWindow.endDay,
                      ]}
                      queryFn={runTableQueryFn}
                      fixedFilters={runTableFixedFilters}
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialPageSize={5}
                      noRowsText={runTableNoRowsText}
                      showToolbar={false}
                      disableColumnSorting
                      disableColumnMenu
                      search={debouncedRunTableSearch}
                      onQueryMeta={setRunTableMeta}
                      columnVisibilityModel={runTableColumnVisibilityModel}
                      onColumnVisibilityModelChange={(nextModel) => {
                        setRunTableColumnVisibilityModel({
                          ...nextModel,
                          runNumber: true,
                        });
                      }}
                    />
                  </Suspense>
                </DetailTablePanel>
              </DeferredRenderBlock>
              <DeferredRenderBlock
                fallback={<DeferredTablePanelSkeleton titleWidth={72} variant="testRuns" />}
                minHeight="24rem"
              >
                <DetailTablePanel
                  tabLabel="Tests"
                  tabsAriaLabel="Suite detail tests panel"
                  toolbarSlot={
                    <TableToolbar<TestSortKey, TestRunStatus>
                      title={undefined}
                      searchAriaLabel={testsTableDefinition.searchAriaLabel}
                      searchValue={debouncedTestsTableSearch}
                      onDebouncedSearchChange={setDebouncedTestsTableSearch}
                      columnVisibilityModel={testsTableColumnVisibilityModel}
                      defaultColumnVisibilityModel={
                        testsTableDefinition.defaultColumnVisibilityModel
                      }
                      columnOptions={testsTableDefinition.columnOptions}
                      onColumnVisibilityModelChange={setTestsTableColumnVisibilityModel}
                      sortKey={testsTableSortKey}
                      sortDirection={testsTableSortDirection}
                      defaultSortKey={testsTableDefinition.defaultSortKey}
                      defaultSortDirection={testsTableDefinition.defaultSortDirection}
                      sortOptions={testsTableDefinition.sortOptions}
                      quickSortOptions={testsTableDefinition.quickSortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setTestsTableSortKey(nextSortKey);
                        setTestsTableSortDirection(nextSortDirection);
                      }}
                      filterStatuses={testsTableFilterStatuses}
                      statusOptions={testsTableStatusOptions}
                      onFilterStatusesChange={setTestsTableFilterStatuses}
                      filterTags={testsTableFilterTags}
                      onFilterTagsChange={setTestsTableFilterTags}
                      dateQuickFilter={detailDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setDetailDateQuickFilter(next);
                        setDetailFromDate(null);
                        setDetailToDate(null);
                      }}
                      fromDate={detailFromDate}
                      toDate={detailToDate}
                      onFromDateChange={setDetailFromDate}
                      onToDateChange={setDetailToDate}
                      availableTags={testsTableAvailableTags}
                      summaryCount={testsTableMeta?.total ?? null}
                      summarySingularLabel="test"
                      summaryPluralLabel="tests"
                    />
                  }
                >
                  <TestsTable
                    persistKey={null}
                    queryKey={[
                      'suite-detail-tests-table',
                      viewModel.suiteId,
                      selectedOrganisationId ?? null,
                      detailProjectId,
                      detailDateQuickFilter,
                      testsTableSortKey,
                      testsTableSortDirection,
                      testsTableStatusFilterKey,
                      testsTableTagFilterKey,
                      debouncedTestsTableSearch,
                      detailDateWindow.startDay,
                      detailDateWindow.endDay,
                    ]}
                    queryFn={testsTableQueryFn}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialPageSize={5}
                    showDeltas={viewModel.showTableDeltas}
                    sparklineRangeDays={detailDateWindow.sparklineRangeDays}
                    metricsStartDay={detailDateWindow.startDay ?? undefined}
                    metricsEndDay={detailDateWindow.endDay ?? undefined}
                    noRowsText={testsTableNoRowsText}
                    showToolbar={false}
                    tagOptionsOverride={viewModel.tagOptions}
                    disableColumnSorting
                    disableColumnMenu
                    showSuiteLinks={false}
                    includeSuiteInNameColumn={false}
                    search={debouncedTestsTableSearch}
                    onQueryMeta={setTestsTableMeta}
                    columnVisibilityModel={testsTableColumnVisibilityModel}
                    onColumnVisibilityModelChange={(nextModel) => {
                      setTestsTableColumnVisibilityModel({
                        ...nextModel,
                        name: true,
                      });
                    }}
                  />
                </DetailTablePanel>
              </DeferredRenderBlock>
            </Stack>
          </Section>
        }
      />
      {editDialog}
    </>
  );
};
